import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';
import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const GeneralSettings = () => {
    const formRef = useRef();

    const [formData, setFormData] = useState({
        facebook: '',
        instagram: '',
        linkedin: '',
        shipping_charges: '',
    });

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings/general');
                if (res.status === 200) {
                    const data = res.data.setting;
                    setFormData({
                        facebook: data.facebook || '',
                        instagram: data.instagram || '',
                        linkedin: data.linkedin || '',
                        shipping_charges: data.shipping_charges ?? '',
                    });
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();

        if (formRef.current && window.$.fn.validate) {
            window.$(formRef.current).validate({
                rules: {
                    facebook: { required: true, url: true },
                    instagram: { required: true, url: true },
                    linkedin: { required: true, url: true },
                    shipping_charges: { required: true, number: true, min: 0 },
                },
                errorElement: 'label',
                validClass: 'is-valid',
                errorClass: 'is-invalid text-danger',
                highlight: function (element, errorClass, validClass) {
                    window.$(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    window.$(element).removeClass(errorClass).addClass(validClass);
                },
            });
        }
    }, []);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            try {
                setSubmitting(true);
                const res = await api.put('/settings/general', {
                    facebook: formData.facebook,
                    instagram: formData.instagram,
                    linkedin: formData.linkedin,
                    shipping_charges: Number(formData.shipping_charges),
                });
                showToast('success', res.data.message, 'Success');
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">General Settings</li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">General Settings</h4>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-4">
                                    <i className="spinner-border text-primary"></i>
                                </div>
                            ) : (
                                <form ref={formRef} noValidate>
                                    <div className="row">

                                        {/* Social Links */}
                                        <div className="col-12 mb-3">
                                            <h5>Social Links</h5>
                                        </div>

                                        {[
                                            { name: 'facebook', label: 'Facebook URL', icon: 'fab fa-facebook' },
                                            { name: 'instagram', label: 'Instagram URL', icon: 'fab fa-instagram' },
                                            { name: 'linkedin', label: 'LinkedIn URL', icon: 'fab fa-linkedin' },
                                        ].map(({ name, label, icon }) => (
                                            <div className="mb-3 col-lg-4 col-md-6" key={name}>
                                                <label className="form-label">
                                                    <i className={`${icon} me-1`}></i> {label}
                                                </label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    name={name}
                                                    value={formData[name]}
                                                    onChange={handleChange}
                                                    placeholder={`https://...`}
                                                />
                                            </div>
                                        ))}

                                        <hr className="my-4" />

                                        {/* Shipping */}
                                        <div className="col-12 mb-3">
                                            <h5>Shipping</h5>
                                        </div>

                                        <div className="mb-3 col-lg-3 col-md-6">
                                            <label className="form-label">
                                                <i className="fas fa-truck me-1"></i> Shipping Charges ($)
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="shipping_charges"
                                                value={formData.shipping_charges}
                                                onChange={handleChange}
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-3">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary me-2"
                                            onClick={handleFormSubmit}
                                            disabled={submitting}
                                        >
                                            {submitting
                                                ? <><i className="spinner-border spinner-border-sm me-1"></i>Saving...</>
                                                : <><i className="fa fa-floppy-disk me-2"></i>Save</>
                                            }
                                        </button>
                                        <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                            <i className="fas fa-times-circle"></i>&nbsp;Cancel
                                        </Link>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GeneralSettings;
